import { useEffect, useState } from 'react';

import { useAuthStore } from '@stores/auth-store';
import { Plan } from '@app-types/service.type';
import { GiftCardRequest } from '@app-types/gift-card-request.type';
import GiftCardGraphic from '@components/gift-card-graphic/gift-card-graphic.component';
import CustomInput from '@components/custom-input/custom-input.component';
import RadioSelector from '@components/radio-selector/radio-selector.component';
import CustomSelect, {
    SelectOption,
} from '@components/custom-select/custom-select.component';
import Header from '@components/header/header.component';
import BasicButton from '@components/basic-button/basic-button.component';
import GoBackView from '@components/go-back-view/go-back-view';
import View from '@components/view/view';
import {
    TNController,
    ToastNotification,
} from '@components/toast-notification/toast-notification.component';
import { paymentsSDK } from '@services/payments';
import { useServicesStore } from '@stores/services-store';

import { useThemeStore } from '../../store';

import styles from './create-gift-card.module.scss';
import { giftCardSchema } from './validation-schema';
import { errorMapper } from './error-mapper';
import { CreatedGiftCardModal } from './containers/created-giftcard-modal/created-giftcard-modal';

const CreateGiftCardView = () => {
    const { theme } = useThemeStore();
    const { permissions, accessToken } = useAuthStore();
    const { fetchServices, services } = useServicesStore();

    const [plans, setPlans] = useState<Plan[]>([]);
    const [giftCard, setGiftCard] = useState<GiftCardRequest>({
        codeType: 'random',
        code: 'CODIGO',
        planId: 0,
    });
    const [turnAround, setTurnAround] = useState(false);
    const [validForm, setValidForm] = useState(false);
    const [creationButton, setCreationButton] = useState({
        text: 'Crear',
        loading: false,
    });
    const [giftCardCreated, setGiftCardCreated] = useState(false);
    const [finalCode, setFinalCode] = useState('');
    const [cleared, setCleared] = useState(false);

    useEffect(() => {
        fetchServices();
    }, []);

    useEffect(() => {
        if (!services.length) return;
        const plans: Plan[] = [];
        for (const service of services) {
            service.plans?.forEach((plan) => {
                if (plan.active) plans.push(plan);
            });
        }
        setPlans(plans);
    }, [services]);

    useEffect(() => {
        setValidForm(giftCardSchema.isValidSync(giftCard));
    }, [giftCard]);

    const updateGiftCard = (attr: string, value: string) => {
        setGiftCard((prev) => ({ ...prev, [attr]: value }));
    };

    const mapPlansToOptions = (plans: Plan[]): SelectOption[] => {
        return plans.map((plan) => ({
            value: plan.id,
            displayText: plan.description,
        }));
    };

    const clearForm = () => {
        setGiftCard({
            codeType: 'random',
            code: '',
            planId: 0,
        });
        setCleared(true);
        setTimeout(() => setCleared(false), 500);
    };

    const onCreation = async () => {
        if (!accessToken) return;
        setCreationButton({ text: 'Cargando...', loading: true });
        const req: any = { planId: giftCard.planId };
        if (giftCard.codeType === 'defined') req.code = giftCard.code;

        const { error, data } = await paymentsSDK.admin.coupons.createGiftCard(
            req,
            accessToken
        );
        setCreationButton({ text: 'Crear', loading: false });
        if (error) {
            const message = errorMapper[error];
            return TNController.show(message, 2500, 'error');
        }
        clearForm();
        setCreationButton({ text: 'Crear', loading: false });
        setGiftCardCreated(true);
        setFinalCode(data!.code);
    };

    return (
        <View permissions={permissions} requiredPermission="read-coupons">
            <Header
                mode={theme}
                title={'Crear Gift Card'}
                subtitle={'Creemos una gift card'}
            />
            <section className={styles.content}>
                <GoBackView />

                <div className={styles.giftCardContainer}>
                    <GiftCardGraphic data={giftCard} turnAround={turnAround} />
                </div>

                <form>
                    <div className={styles.block}>
                        <label>Escoge el plan:</label>
                        <CustomSelect
                            options={mapPlansToOptions(plans)}
                            placeholder={'Escoge el plan'}
                            width="100%"
                            onSelect={(planId) =>
                                updateGiftCard('planId', planId)
                            }
                            clear={cleared}
                        />
                    </div>
                    <div className={styles.block}>
                        <label>Código del Gift Card</label>
                        <RadioSelector
                            name={'code-type'}
                            options={[
                                {
                                    value: 'random',
                                    name: 'Aleatorio',
                                    checked: giftCard.codeType === 'random',
                                },
                                {
                                    value: 'defined',
                                    name: 'Quiero definirlo',
                                    checked: giftCard.codeType === 'defined',
                                },
                            ]}
                            onChange={(codeType) =>
                                updateGiftCard('codeType', codeType)
                            }
                        />
                        {giftCard.codeType === 'defined' && (
                            <CustomInput
                                placeholder={'Código'}
                                width={'100%'}
                                inputColor={'#fff'}
                                defaultValue={giftCard.code}
                                max={20}
                                onFocus={() => setTurnAround(true)}
                                onBlur={() => setTurnAround(false)}
                                onChange={(code) =>
                                    updateGiftCard('code', code)
                                }
                                clear={cleared}
                            />
                        )}
                    </div>
                    <BasicButton
                        width={'100%'}
                        disabled={!validForm}
                        style={{ marginTop: '40px' }}
                        onClick={onCreation}
                        loading={creationButton.loading}
                    >
                        {creationButton.text}
                    </BasicButton>
                </form>
            </section>
            {giftCardCreated && (
                <CreatedGiftCardModal
                    code={finalCode}
                    onConfirm={() => setGiftCardCreated(false)}
                />
            )}
            <ToastNotification />
        </View>
    );
};

export default CreateGiftCardView;
